import { temPermissao } from '@/features/auth/permissions'
import { navItems, type NavItem } from './nav-items'

/**
 * Conjunto de feature flags LIGADAS, do ponto de vista de quem renderiza o
 * menu. Ausente ou vazio ⇒ nenhuma flag ligada: item com `requiresFlag` some.
 * Fail-closed de propósito — chamador que esqueceu de passar o conjunto
 * esconde o módulo, nunca o abre.
 */
export type FlagsAtivas = ReadonlySet<string>

/**
 * Item que declara ao menos uma condição própria de visibilidade.
 *
 * D1.3 — filha SEM nenhuma delas não aparece. Não é "pública" e não herda
 * mais o gate do pai: a herança saiu porque tinha zero consumidores, e o que
 * ela protegia era o caso de AMANHÃ (filha nova sem gate apareceria para todo
 * mundo). O substituto é fail-closed, que dá a mesma garantia sem a regra
 * extra.
 *
 * Lista vazia também não conta como gate: `some` sobre vazio é false, então
 * `[]` recusaria todo mundo, inclusive o admin.
 */
export function temGateProprio(item: NavItem): boolean {
  return Boolean(
    item.requiresFlag ||
      item.requiresPermission ||
      item.requiresAnyPermission?.length ||
      item.requiresAnyGate?.length,
  )
}

export function itemVisivel(item: NavItem, perfis?: string[], flagsAtivas?: FlagsAtivas): boolean {
  if (item.requiresFlag && !flagsAtivas?.has(item.requiresFlag)) return false
  if (item.requiresPermission && !temPermissao(perfis, item.requiresPermission)) return false
  if (item.requiresAnyPermission && !item.requiresAnyPermission.some((p) => temPermissao(perfis, p))) return false
  // Gates alternativos (ver NavItem.requiresAnyGate): basta UM passar, e cada
  // um é permissão + flag opcional. Lista vazia reprova — como em
  // `requiresAnyPermission`, `[].some` é false e o fail-closed é o certo.
  if (
    item.requiresAnyGate &&
    !item.requiresAnyGate.some((g) => (!g.flag || flagsAtivas?.has(g.flag)) && temPermissao(perfis, g.permission))
  ) {
    return false
  }
  return true
}

// Filtra por RBAC (UX apenas) e por feature flag: item sem permissão some do
// menu, e item cuja flag está desligada também. Suporta requiresPermission
// (uma), requiresAnyPermission (basta uma da lista, p/ itens-agregação como
// Configurações), requiresFlag (CLUBE-04) e children (grupo expansível — cada
// filho é filtrado pelo próprio gate).
//
// Item RAIZ sem nenhum gate é visível a todos. FILHO sem gate próprio SOME
// (D1.3) — as duas metades desta frase são opostas de propósito, e o
// comentário anterior só trazia a primeira.
export function filtrarVisiveis(items: NavItem[], perfis?: string[], flagsAtivas?: FlagsAtivas): NavItem[] {
  try {
    return items.flatMap((item) => {
      const proprio = itemVisivel(item, perfis, flagsAtivas)
      if (!item.children?.length) return proprio ? [item] : []

      // D1.3 — A HERANÇA SAIU, e no lugar dela entrou FAIL-CLOSED.
      //
      // A U3 fez "filha sem gate próprio herda o gate do pai" porque
      // `/config/gerais` e `/config/integracoes` não declaravam nada e a união
      // os teria aberto a qualquer perfil. Com a D1.2 os dois passaram a
      // declarar o gate explicitamente (`PERMISSOES_CONFIG`), e a contagem de
      // consumidores da herança foi a ZERO — medido em 30/09/2026, filha por
      // filha, em todo o menu.
      //
      // ⚠️ REMOVER NÃO É DELETAR PARA "VISÍVEL". Trocar isto por
      // `filter(itemVisivel)` puro devolveria exatamente o vazamento da U3:
      // `itemVisivel` não tem condição que reprove um item sem gate, então a
      // filha nova sem gate apareceria para TODO MUNDO. O que a herança
      // protegia não era o caso de hoje — era o caso de amanhã.
      //
      // Então a regra vira a mais simples que preserva a garantia: filha sem
      // gate próprio NÃO aparece. `permissoesDaRota` lança e
      // `RequirePermission` recusa lista vazia — as três camadas NEGAM, cada
      // uma do seu jeito.
      //
      // ⚠️ Elas NÃO são idênticas, e a revisão independente mediu onde:
      // `temGateProprio` aceita `requiresFlag` SOZINHO como gate próprio,
      // enquanto `permissoesDaRota` lança nesse caso. Um item só-com-flag
      // apareceria no menu e derrubaria o import de `routes/index.tsx` se
      // alguém o fiasse numa rota. Hoje não existe — todo item com flag também
      // tem permissão, medido — e o contrato pegaria, mas com mensagem que não
      // aponta a causa. Dito aqui em vez de afirmar uma equivalência que não
      // se sustenta.
      //
      // Hoje isto não muda nada: não há filha sem gate (fixado em
      // `contrato-do-gate.test.tsx`). Amanhã, transforma um vazamento
      // silencioso num item que some — visível na primeira olhada, e sem
      // expor tela nenhuma enquanto ninguém percebe.
      const filhos = item.children.filter(
        (c) => temGateProprio(c) && itemVisivel(c, perfis, flagsAtivas),
      )

      // U3 — O PAI É A UNIÃO DOS FILHOS VISÍVEIS.
      //
      // Antes, o pai era filtrado só pelo gate DELE e os filhos eram filtrados
      // à parte. Isso bastava enquanto todo filho compartilhava o gate do pai.
      // Deixou de bastar quando Ordens de Compra entrou em Compras SEM a flag
      // do catálogo: com o gate antigo, desligar `compras.catalogo.habilitado`
      // faria a OC sumir junto com o pai — uma regressão para quem já a usa,
      // causada por um menu.
      if (!proprio && filhos.length === 0) return []
      if (proprio) return [{ ...item, children: filhos }]

      // O gate próprio do pai não passa, mas algum filho é visível. O pai
      // aparece e seu LINK passa a apontar para o primeiro filho visível —
      // senão clicar em "Compras" com o catálogo desligado levaria à tela do
      // catálogo, que é justamente a que não pode ser aberta. `to` não é
      // sobrescrito porque é a identidade do grupo (ver NavItem.toEfetivo).
      return [{ ...item, toEfetivo: filhos[0].to, children: filhos }]
    })
  } catch {
    // Perfil em formato inesperado não pode derrubar a casca; menu vazio é
    // degradação aceitável, tela em branco não.
    return []
  }
}

// Grupo conta pelo próprio `to`: hoje ninguém enxerga o CRM sem crm:ver (#828).
//
// Sem `flagsAtivas`, item atrás de flag NUNCA é destino inicial (fail-closed).
// Quem repassa o conjunto é `DestinoInicial` (routes/destino-inicial.tsx): tenta
// primeiro SEM flags, depois com a união de flags do menu (clube, canal, compras,
// rede, rastreador), e mostra o spinner enquanto as sondas do rastreador, da Rede
// e do Clube carregam — "sem acesso" só aparece com todas respondidas.
export function primeiraRotaPermitida(
  perfis?: string[],
  itens: NavItem[] = navItems,
  flagsAtivas?: FlagsAtivas,
): string | null {
  // CONTRATO, e não consequência: sem perfil não há destino. A função recebe
  // "quem é" e devolve "para onde mandar"; com ninguém na entrada, a única
  // resposta correta é nenhum lugar — independente de como o menu estiver
  // montado hoje.
  //
  // A guarda NÃO é redundante: um item de topo sem gate próprio (como já foi
  // o teaser de Score & Crédito) mandaria uma conta SEM NENHUMA PERMISSÃO
  // para ele, e nada no diff desse dia pareceria ter a ver com permissão.
  //
  // `temPermissao` já trata perfis vazio como "nenhuma permissão"
  // (permissions.ts:366), inclusive ignorando `permissoesDaSessao`. Esta
  // guarda diz a mesma coisa no lugar onde a pergunta é feita.
  if (!perfis || perfis.length === 0) return null

  const primeiro = filtrarVisiveis(itens, perfis, flagsAtivas).find((item) => !item.disabled)
  return primeiro?.toEfetivo ?? primeiro?.to ?? null
}

export function permissoesDaRota(to: string, itens: NavItem[] = navItems): readonly string[] {
  const item = itens.flatMap((i) => [i, ...(i.children ?? [])]).find((i) => i.to === to)
  if (!item) throw new Error(`rota fora do menu: ${to}`)
  // A UNIÃO: `requiresAnyGate` entra junto porque a ROTA tem de deixar entrar
  // todo visitante que o MENU mostra — quais abas ele vê é decisão da tela.
  const daLista = item.requiresAnyPermission ?? (item.requiresPermission ? [item.requiresPermission] : [])
  const dosGates = item.requiresAnyGate?.map((g) => g.permission) ?? []
  const permissoes = [...new Set([...daLista, ...dosGates])]

  // D1.2 — LISTA VAZIA É ERRO DE PROGRAMAÇÃO, e lançar é o fail-closed certo.
  //
  // `[]` entregue a `RequirePermission` recusa TODO MUNDO, inclusive o admin,
  // porque `[].some(...)` é false. Devolver `[]` em silêncio dava ao chamador
  // duas saídas ruins: montar a rota trancada para todos, ou — o que o
  // `/config` fazia — trocar o `[]` por NENHUM gate, deixando a rota aberta a
  // qualquer sessão. Os dois são o oposto do que se queria.
  //
  // Lançar é a política da função inteira: ela recusa responder o que não
  // sabe, como já fazia para rota fora do menu.
  //
  // ⚠️ A REDE QUE PEGA ISSO, MEDIDA — e NÃO é a suíte. A versão anterior
  // deste comentário afirmava que `routes/index.tsx` "é carregado por toda a
  // suíte de rotas". Medido: ZERO arquivos de teste importam `@/routes` (o
  // único importador é `App.tsx`, importado só por `main.tsx`). A suíte de
  // 279 arquivos nunca avalia este módulo.
  //
  // Quem pega são DOIS portões, cada um para um caso, e nenhum cobre o do
  // outro (medido — ver o quadro em `routes/index.tsx`, ao lado do `.map()`):
  // os testes de intenção pegam módulo novo sem gate — 4 vermelhos em 3
  // arquivos (`contrato-do-gate.test.tsx` ×2, `modulos.test.ts`,
  // `nav-items.test.ts`); o guard de CI
  // `scripts/ci/check_rota_sem_gate.py` pega rota nova escrita sem gate, e é
  // CEGO ao primeiro caso.
  //
  // E o buraco que sobra, dito por inteiro: este `throw` acontece na
  // AVALIAÇÃO DO MÓDULO, antes de o React montar, então nenhum error boundary
  // o pega — o modo de falha é SPA em branco para todo mundo. O guard de CI é
  // o que impede isso de chegar lá, não um teste unitário.
  if (permissoes.length === 0) {
    throw new Error(
      `rota sem gate declarado: ${to}. ` +
        'Declare `requiresPermission` ou `requiresAnyPermission` no item de menu — ' +
        'lista vazia recusa todo mundo, inclusive o admin. ' +
        'Se a seção é aberta a quem já entra no grupo, repita o gate DO GRUPO (ex.: PERMISSOES_CONFIG).',
    )
  }
  return permissoes
}
